import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';

/* ─────────────────────────────────────────────────────────────────────────
 * Stock Inventory — Full port of VBA Stock Inventory.xlsm
 *
 * VBA Features Replicated:
 *   - Component selector buttons (BB_Btn, CIR_Btn, BushBtn, TG_Btn, NB_Btn, CI_Btn)
 *   - Stock In / Stock Out / Transfer tracking
 *   - Show/Hide toggle for quick entry panel
 *   - Stock alerts for items below minimum
 *   - Category-wise summary reports
 * ───────────────────────────────────────────────────────────────────────── */

// ─── Types ───

interface StockItem {
  id: string;
  item_code: string;
  description: string;
  category: string;
  quantity_in: number;
  quantity_out: number;
  available: number;
  unit_price: number;
  location: string;
  min_stock: number;
  status: 'in_stock' | 'low_stock' | 'out_of_stock';
  last_updated: string;
  notes: string;
}

interface StockLog {
  id: string;
  item_code: string;
  category: string;
  type: 'stock_in' | 'stock_out' | 'adjustment' | 'transfer';
  quantity: number;
  from_location: string;
  to_location: string;
  reference: string;
  notes: string;
  timestamp: string;
  user: string;
}

// ─── Constants ───

const CATEGORIES = [
  { code: 'BB', label: 'Ball Bearings', icon: '🔩', color: 'bg-blue-100 text-blue-700', vbaBtn: 'BB_Btn',
    commonItems: ['6201', '6202', '6203', '6204', '6205', '6206', '6305'] },
  { code: 'CIR', label: 'Circlips', icon: '⭕', color: 'bg-emerald-100 text-emerald-700', vbaBtn: 'CIR_Btn',
    commonItems: ['CIR/6201', 'CIR/6202', 'CIR/6203', 'CIR/6204', 'CIR/6205', 'CIR/6206'] },
  { code: 'Bush', label: 'Bushes', icon: '🔧', color: 'bg-purple-100 text-purple-700', vbaBtn: 'BushBtn',
    commonItems: ['GB', 'PB', 'SSB'] },
  { code: 'TG', label: 'Thread Guards', icon: '🛡️', color: 'bg-amber-100 text-amber-700', vbaBtn: 'TG_Btn',
    commonItems: ['PTG', 'ETG', 'MTG', 'LTG'] },
  { code: 'NB', label: 'Nut Bolts', icon: '⚡', color: 'bg-red-100 text-red-700', vbaBtn: 'NB_Btn',
    commonItems: ['NB', 'SSNB'] },
  { code: 'CI', label: 'Circlip Inner', icon: '🎯', color: 'bg-cyan-100 text-cyan-700', vbaBtn: 'CI_Btn',
    commonItems: ['HCI', 'MCIH'] },
  { code: 'Castors', label: 'Castors', icon: '⚙️', color: 'bg-indigo-100 text-indigo-700', vbaBtn: null, commonItems: [] },
  { code: 'Wheels', label: 'Wheels', icon: '🛞', color: 'bg-orange-100 text-orange-700', vbaBtn: null, commonItems: [] },
  { code: 'Spacer', label: 'Spacers', icon: '📏', color: 'bg-pink-100 text-pink-700', vbaBtn: null, commonItems: [] },
  { code: 'FC', label: 'Flanges', icon: '🔩', color: 'bg-teal-100 text-teal-700', vbaBtn: null, commonItems: [] },
  { code: 'Adopter', label: 'Adapters', icon: '🔗', color: 'bg-violet-100 text-violet-700', vbaBtn: null, commonItems: [] },
  { code: 'Axle', label: 'Axles', icon: '↔️', color: 'bg-lime-100 text-lime-700', vbaBtn: null, commonItems: [] },
];

const LOCATIONS = ['Ludhiana Main', 'Ludhiana Store', 'Gurgaon NCR', 'Kolkata Depot', 'Production Floor', 'Raw Material Store'];

const STOCK_TYPES = [
  { code: 'stock_in', label: 'Stock In', icon: '📥', color: 'text-emerald-600' },
  { code: 'stock_out', label: 'Stock Out', icon: '📤', color: 'text-red-600' },
  { code: 'transfer', label: 'Transfer', icon: '🔄', color: 'text-blue-600' },
  { code: 'adjustment', label: 'Adjustment', icon: '✏️', color: 'text-amber-600' },
];

// ─── Helpers ───

function genId(): string { return `stk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`; }

function getStatus(qtyIn: number, qtyOut: number, minStock: number): StockItem['status'] {
  const avail = qtyIn - qtyOut;
  if (avail <= 0) return 'out_of_stock';
  if (avail <= minStock) return 'low_stock';
  return 'in_stock';
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; border: string }> = {
  in_stock: { label: 'In Stock', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  low_stock: { label: 'Low Stock', bg: 'bg-amber-50', border: 'border-amber-200' },
  out_of_stock: { label: 'Out of Stock', bg: 'bg-red-50', border: 'border-red-200' },
};

// ─── Seed Data (common VBA component items) ───

function getSeedItems(): StockItem[] {
  const now = new Date().toISOString();
  type SeedDraft = Omit<StockItem, 'id' | 'status'>;
  const seeds: SeedDraft[] = [
    { item_code: '6203', description: 'Ball Bearing 6203', category: 'BB', quantity_in: 200, quantity_out: 45, available: 155, unit_price: 70, location: 'Ludhiana Main', min_stock: 50, last_updated: now, notes: 'Most common bearing size' },
    { item_code: '6205', description: 'Ball Bearing 6205', category: 'BB', quantity_in: 150, quantity_out: 30, available: 120, unit_price: 103, location: 'Ludhiana Main', min_stock: 40, last_updated: now, notes: '' },
    { item_code: '6204', description: 'Ball Bearing 6204', category: 'BB', quantity_in: 100, quantity_out: 15, available: 85, unit_price: 91, location: 'Gurgaon NCR', min_stock: 30, last_updated: now, notes: '' },
    { item_code: '6206', description: 'Ball Bearing 6206', category: 'BB', quantity_in: 80, quantity_out: 10, available: 70, unit_price: 156, location: 'Ludhiana Main', min_stock: 25, last_updated: now, notes: 'Heavy duty' },
    { item_code: 'CIR/6203', description: 'Circlip for 6203', category: 'CIR', quantity_in: 500, quantity_out: 120, available: 380, unit_price: 10, location: 'Ludhiana Store', min_stock: 100, last_updated: now, notes: '' },
    { item_code: 'CIR/6205', description: 'Circlip for 6205', category: 'CIR', quantity_in: 400, quantity_out: 80, available: 320, unit_price: 12, location: 'Ludhiana Store', min_stock: 80, last_updated: now, notes: '' },
    { item_code: 'GB', description: 'GF Bush', category: 'Bush', quantity_in: 300, quantity_out: 60, available: 240, unit_price: 35, location: 'Production Floor', min_stock: 75, last_updated: now, notes: 'General purpose' },
    { item_code: 'PB', description: 'Pipe Bush', category: 'Bush', quantity_in: 200, quantity_out: 40, available: 160, unit_price: 40, location: 'Production Floor', min_stock: 50, last_updated: now, notes: '' },
    { item_code: 'PTG', description: 'Plain Thread Guard', category: 'TG', quantity_in: 250, quantity_out: 50, available: 200, unit_price: 18, location: 'Ludhiana Store', min_stock: 60, last_updated: now, notes: '' },
    { item_code: 'ETG', description: 'Extended Thread Guard', category: 'TG', quantity_in: 150, quantity_out: 35, available: 115, unit_price: 22, location: 'Ludhiana Store', min_stock: 40, last_updated: now, notes: '' },
    { item_code: 'NB', description: 'Standard Nut Bolt', category: 'NB', quantity_in: 1000, quantity_out: 250, available: 750, unit_price: 8, location: 'Raw Material Store', min_stock: 200, last_updated: now, notes: '' },
    { item_code: 'SSNB', description: 'SS Nut Bolt', category: 'NB', quantity_in: 300, quantity_out: 45, available: 255, unit_price: 15, location: 'Raw Material Store', min_stock: 75, last_updated: now, notes: 'Stainless steel' },
    { item_code: 'HCI', description: 'Heavy CI', category: 'CI', quantity_in: 180, quantity_out: 30, available: 150, unit_price: 25, location: 'Ludhiana Main', min_stock: 40, last_updated: now, notes: '' },
    { item_code: 'MCIH', description: 'Medium CI Heavy', category: 'CI', quantity_in: 120, quantity_out: 20, available: 100, unit_price: 20, location: 'Ludhiana Main', min_stock: 30, last_updated: now, notes: '' },
  ];
  return seeds.map(s => ({ ...s, id: genId(), status: getStatus(s.quantity_in, s.quantity_out, s.min_stock) }));
}

// ─── Main Component ───

export default function StockInventoryPage() {
  const [items, setItems] = useState<StockItem[]>([]);
  const [logs, setLogs] = useState<StockLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState<'inventory' | 'log' | 'alerts' | 'report'>('inventory');
  const [showQuickEntry, setShowQuickEntry] = useState(true); // VBA Toggle_Show

  // Quick Entry form (replicates VBA component buttons)
  const [quickCategory, setQuickCategory] = useState('');
  const [quickItemCode, setQuickItemCode] = useState('');
  const [quickQty, setQuickQty] = useState<number>(1);
  const [quickType, setQuickType] = useState<'stock_in' | 'stock_out'>('stock_in');
  const [quickLocation, setQuickLocation] = useState('');
  const [quickRef, setQuickRef] = useState('');

  // Full form
  const [showFullForm, setShowFullForm] = useState(false);
  const [editingItem, setEditingItem] = useState<StockItem | null>(null);
  const [form, setForm] = useState({ item_code: '', description: '', category: '', quantity_in: 0, quantity_out: 0, unit_price: 0, location: '', min_stock: 10, notes: '' });

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    try {
      const [stockRes, logRes] = await Promise.all([
        supabase.from('stock_inventory').select('*').order('item_code'),
        supabase.from('stock_logs').select('*').order('timestamp', { ascending: false }).limit(100),
      ]);
      if (stockRes.data && stockRes.data.length > 0) {
        setItems(stockRes.data.map((d: any) => ({ ...d, status: getStatus(d.quantity_in, d.quantity_out, d.min_stock) })));
      } else {
        // Seed with common VBA component data
        setItems(getSeedItems());
      }
      if (logRes.data) setLogs(logRes.data);
    } catch {
      setItems(getSeedItems());
    }
    setLoading(false);
  }

  // ─── Filtered data ───
  const filtered = useMemo(() => items.filter(item => {
    const matchSearch = !search || item.item_code.toLowerCase().includes(search.toLowerCase()) || item.description.toLowerCase().includes(search.toLowerCase());
    const matchCat = !filterCategory || item.category === filterCategory;
    const matchStatus = !filterStatus || item.status === filterStatus;
    return matchSearch && matchCat && matchStatus;
  }), [items, search, filterCategory, filterStatus]);

  const stats = useMemo(() => {
    const total = items.length;
    const inStock = items.filter(i => i.status === 'in_stock').length;
    const lowStock = items.filter(i => i.status === 'low_stock').length;
    const outOfStock = items.filter(i => i.status === 'out_of_stock').length;
    const totalValue = items.reduce((s, i) => s + ((i.quantity_in - i.quantity_out) * i.unit_price), 0);
    const alerts = items.filter(i => i.status === 'low_stock' || i.status === 'out_of_stock');
    return { total, inStock, lowStock, outOfStock, totalValue, alerts };
  }, [items]);

  const categoryStats = useMemo(() => {
    return CATEGORIES.map(c => {
      const catItems = items.filter(i => i.category === c.code);
      const totalQty = catItems.reduce((s, i) => s + (i.quantity_in - i.quantity_out), 0);
      const totalValue = catItems.reduce((s, i) => s + ((i.quantity_in - i.quantity_out) * i.unit_price), 0);
      return { ...c, count: catItems.length, totalQty, totalValue };
    }).filter(c => c.count > 0);
  }, [items]);

  // ─── Quick Entry (replicates VBA BB_Btn, CIR_Btn, etc.) ───
  function handleQuickCategoryClick(catCode: string) {
    setQuickCategory(catCode);
    setQuickItemCode('');
  }

  function handleQuickSubmit() {
    if (!quickItemCode || quickQty <= 0) return;

    const existing = items.find(i => i.item_code === quickItemCode && i.category === quickCategory);
    const now = new Date().toISOString();

    if (existing) {
      const updated = { ...existing };
      if (quickType === 'stock_in') {
        updated.quantity_in += quickQty;
      } else {
        updated.quantity_out += quickQty;
      }
      updated.available = updated.quantity_in - updated.quantity_out;
      updated.status = getStatus(updated.quantity_in, updated.quantity_out, updated.min_stock);
      updated.last_updated = now;
      setItems(prev => prev.map(i => i.id === existing.id ? updated : i));
    } else {
      const qtyIn = quickType === 'stock_in' ? quickQty : 0;
      const qtyOut = quickType === 'stock_out' ? quickQty : 0;
      const newItem: StockItem = {
        id: genId(), item_code: quickItemCode.toUpperCase(), description: quickItemCode,
        category: quickCategory,
        quantity_in: qtyIn,
        quantity_out: qtyOut,
        available: qtyIn - qtyOut,
        unit_price: 0, location: quickLocation || 'Ludhiana Main', min_stock: 10,
        status: getStatus(qtyIn, qtyOut, 10),
        last_updated: now, notes: quickRef,
      };
      setItems(prev => [...prev, newItem]);
    }

    // Log the transaction
    const log: StockLog = {
      id: genId(), item_code: quickItemCode.toUpperCase(), category: quickCategory,
      type: quickType, quantity: quickQty,
      from_location: quickType === 'stock_out' ? (quickLocation || 'Ludhiana Main') : '',
      to_location: quickType === 'stock_in' ? (quickLocation || 'Ludhiana Main') : '',
      reference: quickRef, notes: '', timestamp: now, user: 'Administrator',
    };
    setLogs(prev => [log, ...prev]);

    // Reset
    setQuickQty(1);
    setQuickRef('');
  }

  // ─── Full CRUD ───
  function openAdd() { setEditingItem(null); setForm({ item_code: '', description: '', category: '', quantity_in: 0, quantity_out: 0, unit_price: 0, location: '', min_stock: 10, notes: '' }); setShowFullForm(true); }
  function openEdit(item: StockItem) { setEditingItem(item); setForm({ item_code: item.item_code, description: item.description, category: item.category, quantity_in: item.quantity_in, quantity_out: item.quantity_out, unit_price: item.unit_price, location: item.location, min_stock: item.min_stock, notes: item.notes }); setShowFullForm(true); }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!form.item_code.trim() || !form.category) { alert('Item Code and Category required.'); return; }
    const available = form.quantity_in - form.quantity_out;
    const status = getStatus(form.quantity_in, form.quantity_out, form.min_stock);
    const record = { ...form, item_code: form.item_code.trim().toUpperCase(), available, status, last_updated: new Date().toISOString() };
    try {
      if (editingItem) { await supabase.from('stock_inventory').update(record).eq('id', editingItem.id); }
      else { await supabase.from('stock_inventory').insert(record); }
      await loadData();
    } catch {
      if (editingItem) { setItems(prev => prev.map(i => i.id === editingItem.id ? { ...record, id: editingItem.id } : i)); }
      else { setItems(prev => [...prev, { ...record, id: genId() }]); }
    }
    setShowFullForm(false);
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this item?')) return;
    try { await supabase.from('stock_inventory').delete().eq('id', id); } catch { /* */ }
    setItems(prev => prev.filter(i => i.id !== id));
  }

  const selectedCat = CATEGORIES.find(c => c.code === quickCategory);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl shadow-elevated p-6 sm:p-8 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-300 uppercase tracking-wider mb-1">
            <span>📦</span><span>Stock Inventory — VBA Port</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Component Inventory</h1>
          <p className="text-slate-300 text-sm mt-1">Track stock for BB, CIR, Bush, TG, NB, CI and all castor wheel components</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowQuickEntry(!showQuickEntry)}
            className={`px-4 py-2 text-xs font-bold rounded-xl border transition-all cursor-pointer ${showQuickEntry ? 'bg-white/20 border-white/40 text-white' : 'bg-white/10 border-white/20 text-white/70'}`}>
            {showQuickEntry ? 'Hide' : 'Show'} Quick Entry
          </button>
          <button onClick={openAdd} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow-lg shadow-blue-600/30 transition-all cursor-pointer">
            + Add Item
          </button>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatBox label="Total Items" value={stats.total} icon="📦" />
        <StatBox label="In Stock" value={stats.inStock} icon="✅" color="text-emerald-600" />
        <StatBox label="Low Stock" value={stats.lowStock} icon="⚠️" color="text-amber-600" />
        <StatBox label="Out of Stock" value={stats.outOfStock} icon="❌" color="text-red-600" />
        <StatBox label="Total Value" value={`₹${stats.totalValue.toLocaleString('en-IN')}`} icon="💰" />
      </div>

      {/* ─── Quick Entry Panel (VBA Component Buttons) ─── */}
      {showQuickEntry && (
        <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6 space-y-4 animate-fadeIn">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">⚡ Quick Stock Entry</h2>
            <span className="text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-lg">
              VBA: Toggle_Show Panel
            </span>
          </div>

          {/* VBA-style component category buttons */}
          <div>
            <label className="block text-[10px] font-bold text-slate-600 mb-2 uppercase">Select Component Type</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.filter(c => c.vbaBtn).map(c => (
                <button key={c.code} onClick={() => handleQuickCategoryClick(c.code)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer ${
                    quickCategory === c.code
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                      : 'border-slate-200 hover:border-blue-300 text-slate-600'
                  }`}>
                  <span>{c.icon}</span>
                  <span>{c.label}</span>
                  <span className="text-[9px] px-1 py-0.5 bg-slate-100 rounded font-mono">{c.code}</span>
                </button>
              ))}
              {/* Extra non-VBA categories */}
              {CATEGORIES.filter(c => !c.vbaBtn).slice(0, 4).map(c => (
                <button key={c.code} onClick={() => handleQuickCategoryClick(c.code)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border-2 transition-all cursor-pointer ${
                    quickCategory === c.code
                      ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                      : 'border-slate-200 hover:border-blue-300 text-slate-600'
                  }`}>
                  <span>{c.icon}</span><span>{c.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Quick entry form */}
          {quickCategory && (
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 animate-fadeIn space-y-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{selectedCat?.icon}</span>
                <span className="text-xs font-bold text-slate-700">Add stock for: {selectedCat?.label}</span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {/* Item Code */}
                <div className="sm:col-span-2">
                  <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Item Code</label>
                  <div className="flex gap-1">
                    <input type="text" value={quickItemCode} onChange={e => setQuickItemCode(e.target.value.toUpperCase())}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-xl text-sm font-mono font-bold focus:border-blue-500 bg-white"
                      placeholder={selectedCat?.commonItems[0] || 'Item Code'} />
                  </div>
                  {/* Quick select common items */}
                  {selectedCat && selectedCat.commonItems.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {selectedCat.commonItems.map(item => (
                        <button key={item} onClick={() => setQuickItemCode(item)}
                          className="px-2 py-0.5 text-[9px] font-bold bg-white border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer">
                          {item}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Type */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Type</label>
                  <div className="flex gap-1">
                    <button onClick={() => setQuickType('stock_in')}
                      className={`flex-1 px-2 py-2 rounded-xl text-[10px] font-bold border transition-all cursor-pointer ${quickType === 'stock_in' ? 'bg-emerald-500 text-white border-emerald-500' : 'border-slate-300 hover:border-emerald-300'}`}>
                      📥 In
                    </button>
                    <button onClick={() => setQuickType('stock_out')}
                      className={`flex-1 px-2 py-2 rounded-xl text-[10px] font-bold border transition-all cursor-pointer ${quickType === 'stock_out' ? 'bg-red-500 text-white border-red-500' : 'border-slate-300 hover:border-red-300'}`}>
                      📤 Out
                    </button>
                  </div>
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Qty</label>
                  <input type="number" min="1" value={quickQty} onChange={e => setQuickQty(parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-bold text-center focus:border-blue-500 bg-white" />
                </div>

                {/* Submit */}
                <div className="flex items-end">
                  <button onClick={handleQuickSubmit} disabled={!quickItemCode || quickQty <= 0}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md disabled:opacity-40 transition-all cursor-pointer">
                    {quickType === 'stock_in' ? '📥 Add Stock' : '📤 Remove Stock'}
                  </button>
                </div>
              </div>

              {/* Reference & Location */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Reference / PO No</label>
                  <input type="text" value={quickRef} onChange={e => setQuickRef(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 bg-white" placeholder="e.g. PO-2026-001" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-600 mb-1 uppercase">Location</label>
                  <select value={quickLocation} onChange={e => setQuickLocation(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500 bg-white">
                    <option value="">Ludhiana Main (default)</option>
                    {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Tabs ─── */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
        {[
          { id: 'inventory' as any, label: '📦 Inventory', count: filtered.length },
          { id: 'log' as any, label: '📋 Stock Log', count: logs.length },
          { id: 'alerts' as any, label: '⚠️ Alerts', count: stats.alerts.length },
          { id: 'report' as any, label: '📊 Category Report' },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
            className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
              activeTab === tab.id ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tab.label} {tab.count !== undefined && <span className="ml-1 text-[9px] px-1.5 py-0.5 bg-slate-200 rounded-full">{tab.count}</span>}
          </button>
        ))}
      </div>

      {/* ─── Inventory Table ─── */}
      {activeTab === 'inventory' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="🔍 Search item code or description..." className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl text-sm focus:border-blue-500" />
            <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-xl text-sm font-bold">
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c.code} value={c.code}>{c.icon} {c.label}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-xl text-sm font-bold">
              <option value="">All Status</option>
              <option value="in_stock">✅ In Stock</option>
              <option value="low_stock">⚠️ Low Stock</option>
              <option value="out_of_stock">❌ Out of Stock</option>
            </select>
          </div>

          <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 overflow-hidden">
            {loading ? (
              <div className="py-16 flex justify-center"><div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" /></div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center"><div className="text-4xl mb-3">📦</div><p className="text-sm font-bold text-slate-500">No items found</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold text-slate-600">Item Code</th>
                      <th className="px-4 py-3 text-left font-bold text-slate-600">Description</th>
                      <th className="px-3 py-3 text-center font-bold text-slate-600">Category</th>
                      <th className="px-3 py-3 text-right font-bold text-slate-600">In</th>
                      <th className="px-3 py-3 text-right font-bold text-slate-600">Out</th>
                      <th className="px-3 py-3 text-right font-bold text-slate-600">Available</th>
                      <th className="px-3 py-3 text-right font-bold text-slate-600">Price</th>
                      <th className="px-3 py-3 text-center font-bold text-slate-600">Status</th>
                      <th className="px-3 py-3 text-left font-bold text-slate-600">Location</th>
                      <th className="px-3 py-3 text-center font-bold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map(item => {
                      const available = item.quantity_in - item.quantity_out;
                      const cat = CATEGORIES.find(c => c.code === item.category);
                      const st = STATUS_CONFIG[item.status];
                      return (
                        <tr key={item.id} className={`hover:bg-slate-50/70 ${item.status === 'out_of_stock' ? 'bg-red-50/30' : item.status === 'low_stock' ? 'bg-amber-50/30' : ''}`}>
                          <td className="px-4 py-3 font-mono font-black text-slate-900">{item.item_code}</td>
                          <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{item.description}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${cat?.color || 'bg-slate-100 text-slate-600'}`}>
                              {cat?.icon} {item.category}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-right font-bold text-emerald-700">{item.quantity_in}</td>
                          <td className="px-3 py-3 text-right font-bold text-red-600">{item.quantity_out}</td>
                          <td className="px-3 py-3 text-right font-black text-slate-900">{available}</td>
                          <td className="px-3 py-3 text-right text-slate-600">₹{item.unit_price}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${st.bg} ${st.border} ${item.status === 'out_of_stock' ? 'text-red-700' : item.status === 'low_stock' ? 'text-amber-700' : 'text-emerald-700'}`}>
                              {st.label}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-[11px] text-slate-500 truncate max-w-[120px]">{item.location || '—'}</td>
                          <td className="px-3 py-3 text-center space-x-1">
                            <button onClick={() => openEdit(item)} className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded text-[10px] font-bold transition-colors cursor-pointer">Edit</button>
                            <button onClick={() => handleDelete(item.id)} className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-600 rounded text-[10px] font-bold transition-colors cursor-pointer">Del</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─── Stock Log ─── */}
      {activeTab === 'log' && (
        <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">📋 Stock Transaction Log</h2>
          {logs.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-400">No transactions yet. Use Quick Entry to record stock movements.</div>
          ) : (
            <div className="space-y-2">
              {logs.slice(0, 50).map(log => {
                const typeConfig = STOCK_TYPES.find(t => t.code === log.type);
                return (
                  <div key={log.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-200/80">
                    <span className="text-lg">{typeConfig?.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-slate-900">{log.item_code}</span>
                        <span className={`text-[10px] font-bold ${typeConfig?.color}`}>{typeConfig?.label}</span>
                      </div>
                      {log.reference && <p className="text-[10px] text-slate-500">Ref: {log.reference}</p>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className={`text-sm font-black ${log.type === 'stock_in' ? 'text-emerald-600' : log.type === 'stock_out' ? 'text-red-600' : 'text-blue-600'}`}>
                        {log.type === 'stock_out' ? '-' : '+'}{log.quantity}
                      </div>
                      <div className="text-[9px] text-slate-400">{new Date(log.timestamp).toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── Alerts ─── */}
      {activeTab === 'alerts' && (
        <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">⚠️ Stock Alerts</h2>
          {stats.alerts.length === 0 ? (
            <div className="py-8 text-center"><span className="text-3xl">✅</span><p className="text-sm font-bold text-emerald-600 mt-2">All items are adequately stocked!</p></div>
          ) : (
            <div className="space-y-2">
              {stats.alerts.map(item => {
                const available = item.quantity_in - item.quantity_out;
                const isOut = item.status === 'out_of_stock';
                return (
                  <div key={item.id} className={`flex items-center gap-3 p-3 rounded-xl border ${isOut ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                    <span className="text-xl">{isOut ? '❌' : '⚠️'}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs">{item.item_code}</span>
                        <span className="text-[10px] text-slate-500">{item.description}</span>
                      </div>
                      <p className="text-[10px] text-slate-500">Min: {item.min_stock} | Available: <span className={`font-black ${isOut ? 'text-red-600' : 'text-amber-600'}`}>{available}</span></p>
                    </div>
                    <button onClick={() => { setQuickCategory(item.category); setQuickItemCode(item.item_code); setQuickType('stock_in'); setActiveTab('inventory'); setShowQuickEntry(true); }}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[10px] font-bold transition-colors cursor-pointer">
                      Restock →
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── Category Report ─── */}
      {activeTab === 'report' && (
        <div className="bg-white rounded-2xl shadow-card border border-slate-200/80 p-6">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">📊 Category Summary</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {categoryStats.map(c => (
              <div key={c.code} className="p-4 rounded-xl bg-slate-50 border border-slate-200/80">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">{c.icon}</span>
                  <div>
                    <p className="text-xs font-bold text-slate-900">{c.label}</p>
                    <p className="text-[10px] text-slate-500">{c.count} items</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase">Qty Available</p>
                    <p className="font-black text-slate-900">{c.totalQty}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase">Value</p>
                    <p className="font-black text-slate-900">₹{c.totalValue.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Add/Edit Modal ─── */}
      {showFullForm && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl border border-slate-200 my-8">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="text-lg font-bold text-slate-900">{editingItem ? 'Edit' : 'Add'} Stock Item</h3>
              <button onClick={() => setShowFullForm(false)} className="text-slate-400 hover:text-slate-600 font-bold text-lg cursor-pointer">✕</button>
            </div>
            <form onSubmit={handleSave} className="space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Item Code *</label>
                  <input required value={form.item_code} onChange={e => setForm({ ...form, item_code: e.target.value.toUpperCase() })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-mono font-bold focus:border-blue-500" />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Category *</label>
                  <select required value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm font-bold focus:border-blue-500">
                    <option value="">Select</option>
                    {CATEGORIES.map(c => <option key={c.code} value={c.code}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block font-bold text-slate-700 mb-1">Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Qty In</label>
                  <input type="number" min="0" value={form.quantity_in} onChange={e => setForm({ ...form, quantity_in: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Qty Out</label>
                  <input type="number" min="0" value={form.quantity_out} onChange={e => setForm({ ...form, quantity_out: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Available</label>
                  <div className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-sm font-black">{form.quantity_in - form.quantity_out}</div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Unit Price (₹)</label>
                  <input type="number" step="0.01" min="0" value={form.unit_price} onChange={e => setForm({ ...form, unit_price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Min Stock</label>
                  <input type="number" min="0" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:border-blue-500" />
                </div>
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Location</label>
                  <select value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl text-xs focus:border-blue-500">
                    <option value="">Select</option>
                    {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button type="button" onClick={() => setShowFullForm(false)} className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md cursor-pointer">{editingItem ? 'Update' : 'Add Item'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, icon, color }: { label: string; value: string | number; icon: string; color?: string }) {
  return (
    <div className="bg-white rounded-xl p-4 shadow-card border border-slate-200/80">
      <div className="flex items-center justify-between mb-1"><span className="text-lg">{icon}</span></div>
      <div className={`text-lg font-black ${color || 'text-slate-900'}`}>{value}</div>
      <div className="text-[10px] font-bold text-slate-500">{label}</div>
    </div>
  );
}
